import * as THREE from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';

/**
 * 按材质累积线段顶点，最后每种材质只产出一个 LineSegments。
 * 几何体数量从「构件数 × 轮廓层数」降到「材质数」，buffer 的创建、上传、销毁和 draw call 都随之减少。
 * z 直接烧进顶点，所以分属不同层的线也能合进同一个对象。
 *
 * 虚线不要放进批次：虚线相位在一个对象内是沿线段累计的，合并会让各段的相位互相串台。
 */
export type TLineBatches = Map<THREE.Material, number[]>;

export const createLineBatches = (): TLineBatches => new Map();

export const getLineBatch = (batches: TLineBatches, material: THREE.Material) => {
  let positions = batches.get(material);
  if (!positions) {
    positions = [];
    batches.set(material, positions);
  }
  return positions;
};

/** 局部坐标的构件（门窗）合并前要自己变换到世界坐标，与 three 的 T·R·S 合成顺序一致 */
export interface ILocalTransform {
  cos: number;
  sin: number;
  centerX: number;
  centerY: number;
  flip: boolean;
}

export const createLocalTransform = (center: IPoint2d, rotation: number, flip?: boolean): ILocalTransform => ({
  cos: Math.cos(rotation),
  sin: Math.sin(rotation),
  centerX: center.x / pixelRatio2d,
  centerY: center.y / pixelRatio2d,
  flip: !!flip
});

// 变换用的暂存量，避免每个顶点都分配对象
let _x = 0;
let _y = 0;

const transformInto = (point: IPoint2d, transform?: ILocalTransform) => {
  const x = point.x / pixelRatio2d;
  const y = point.y / pixelRatio2d;

  if (!transform) {
    _x = x;
    _y = y;
    return;
  }

  // 先按 x 取反（对应 scale.x = -1），再旋转，再平移
  const flippedX = transform.flip ? -x : x;
  _x = flippedX * transform.cos - y * transform.sin + transform.centerX;
  _y = flippedX * transform.sin + y * transform.cos + transform.centerY;
};

/**
 * 合并是有代价的：任何一个 NaN 顶点都会让整条 buffer 的包围球失效，
 * 于是整个 LineSegments 被视锥剔除，取景用的场景包围盒也会算成 NaN 而出空图。
 * 所以入口就把非法线段挡掉，坏数据只损失它自己那一段。
 */
export const pushSegment = (
  positions: number[],
  start: IPoint2d,
  end: IPoint2d,
  z: number,
  transform?: ILocalTransform
) => {
  if (!Number.isFinite(z)) return;

  transformInto(start, transform);
  const startX = _x;
  const startY = _y;
  if (!Number.isFinite(startX) || !Number.isFinite(startY)) return;

  transformInto(end, transform);
  if (!Number.isFinite(_x) || !Number.isFinite(_y)) return;

  positions.push(startX, startY, z, _x, _y, z);
};

/** 闭合折线：首尾相连 */
export const pushClosedLoop = (positions: number[], outline: IPoint2d[], z: number, transform?: ILocalTransform) => {
  const len = outline.length;
  for (let i = 0; i < len; i++) {
    pushSegment(positions, outline[i], outline[(i + 1) % len], z, transform);
  }
};

/** 开放折线：不闭合 */
export const pushPolyline = (positions: number[], outline: IPoint2d[], z: number, transform?: ILocalTransform) => {
  for (let i = 0; i < outline.length - 1; i++) {
    pushSegment(positions, outline[i], outline[i + 1], z, transform);
  }
};

export const addBatchedLines = (scene: THREE.Scene, batches: TLineBatches) => {
  for (const [material, positions] of batches) {
    if (!positions.length) continue;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const line = new THREE.LineSegments(geometry, material);
    // 合并后的线段是全图级的聚合对象，逐对象剔除没有收益，
    // 关掉它可以顺便杜绝「包围球异常 -> 整批线消失」这种连锁失效
    line.frustumCulled = false;
    scene.add(line);
  }
};

/** 独立的一条折线，给虚线这类不能合批的场景用 */
export const createLineSegments = (points: IPoint2d[], material: THREE.Material, z: number, closed = false) => {
  const positions: number[] = [];
  if (closed) {
    pushClosedLoop(positions, points, z);
  } else {
    pushPolyline(positions, points, z);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

  return new THREE.LineSegments(geometry, material);
};
